import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePlayerProgression } from "@/hooks/usePlayerProgression";
import { useFriends, FriendStats, Friendship } from "@/hooks/useFriends";
import { useProfile } from "@/hooks/useProfile";
import OnboardingProfileSetup from "@/components/onboarding/OnboardingProfileSetup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  Search, UserPlus, Users, Check, X, Swords, Trophy,
  Flame, BookOpen, Star, Clock, Zap, BarChart3, AlertCircle, Loader2,
  UsersRound, Activity, Sparkles,
} from "lucide-react";
import PageShell from "@/components/premium/PageShell";
import PageHeader from "@/components/premium/PageHeader";
import { GuidesAvailableStrip } from "@/components/social/InstructorOpponents";
import type { AnyValue } from "@/lib/rowTypes";

type Tab = "friends" | "requests";

const FriendsScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { playerLevel } = usePlayerProgression();
  const { profile, complete: profileComplete, refetch: refetchProfile } = useProfile();
  const {
    friends, pendingReceived, pendingSent, loading,
    searchUsers, sendRequest, acceptRequest, declineRequest, removeFriend,
    getFriendStats, sendChallenge,
  } = useFriends();

  const [tab, setTab] = useState<Tab>("friends");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Record<string, AnyValue>[]>([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [sentTo, setSentTo] = useState<Set<string>>(new Set());
  const [compareId, setCompareId] = useState<string | null>(null);
  const [compareStats, setCompareStats] = useState<FriendStats | null>(null);
  const [compareName, setCompareName] = useState("");
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [challengeTarget, setChallengeTarget] = useState<string | null>(null);
  const [showProfileGate, setShowProfileGate] = useState(false);

  const handleSearch = async () => {
    if (searchQuery.trim().length < 2) return;
    setSearching(true);
    setSearched(false);
    const results = await searchUsers(searchQuery.trim());
    // Exclude existing friends and pending people
    const knownIds = new Set<string>([
      ...friends.map(f => f.friend_profile?.id || ""),
      ...pendingSent.map(f => f.friend_profile?.id || ""),
      ...pendingReceived.map(f => f.friend_profile?.id || ""),
    ]);
    setSearchResults(results.filter((r: AnyValue) => r.id !== user?.id && !knownIds.has(r.id)));
    setSearched(true);
    setSearching(false);
  };

  const handleSend = async (friendId: string) => {
    if (!profileComplete) {
      setShowProfileGate(true);
      return;
    }
    await sendRequest(friendId);
    setSentTo(prev => new Set(prev).add(friendId));
  };

  const handleCompare = async (friendId: string, name: string) => {
    setCompareId(friendId);
    setCompareName(name);
    setLoadingCompare(true);
    const stats = await getFriendStats(friendId);
    setCompareStats(stats);
    setLoadingCompare(false);
  };

  const handleSendChallenge = async (
    fId: string,
    type: "xp_race" | "quiz_duel",
    duration?: number,
  ) => {
    if (!profileComplete) {
      setShowProfileGate(true);
      return;
    }
    await sendChallenge(fId, type, duration);
    setChallengeTarget(null);
  };

  const pendingCount = pendingReceived.length;
  const friendsCount = friends.length;
  const sentCount = pendingSent.length;

  if (loading) {
    return (
      <PageShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Community"
        subtitle="Connect with friends, follow progress, and grow with others on the same journey."
        icon={Users}
      />

        {/* Premium snapshot strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <SnapshotCard
            icon={UsersRound}
            label="Friends"
            value={friendsCount}
            sub={friendsCount === 1 ? "connected learner" : "connected learners"}
            tone="primary"
          />
          <SnapshotCard
            icon={UserPlus}
            label="Requests"
            value={pendingCount}
            sub={pendingCount === 0 ? "no pending requests" : "awaiting your reply"}
            tone="accent"
          />
          <SnapshotCard
            icon={Clock}
            label="Sent"
            value={sentCount}
            sub={sentCount === 0 ? "no outgoing requests" : "awaiting response"}
            tone="muted"
          />
          <SnapshotCard
            icon={Sparkles}
            label="Social Hub"
            value={friendsCount + pendingCount}
            sub="people in your circle"
            tone="secondary"
          />
        </div>

        {/* Always-available practice opponents */}
        <GuidesAvailableStrip />

        {/* Profile completion banner */}
        {!profileComplete && (
          <div className="glass-card p-4 border border-primary/30 bg-primary/5">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-heading font-semibold text-foreground mb-1">Finish your profile</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Add a display name and avatar so friends can find you and challenges work properly.
                </p>
                <Button size="sm" onClick={() => setShowProfileGate(true)}>
                  Complete profile
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs — horizontal scroll on tiny screens to prevent overflow */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-3 px-3 sm:mx-0 sm:px-0">
          {([
            { key: "friends" as Tab, label: "Friends", icon: Users, badge: 0 },
            { key: "requests" as Tab, label: "Requests", icon: UserPlus, badge: pendingCount },
          ]).map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                "flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap shrink-0",
                tab === t.key ? "bg-primary/10 text-primary border border-primary/30" : "text-muted-foreground hover:text-foreground border border-transparent",
              )}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.badge > 0 && (
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold">{t.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* FRIENDS TAB */}
        {tab === "friends" && (
          <div className="space-y-4">
            {/* Search */}
            <div className="glass-card p-3 sm:p-4 space-y-3">
              <p className="text-sm font-medium text-foreground">Add Friend</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Search by name or @username…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                  className="flex-1 min-w-0"
                />
                <Button size="sm" onClick={handleSearch} disabled={searching || searchQuery.trim().length < 2} className="shrink-0">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {searchResults.length > 0 && (
                <div className="space-y-2">
                  {searchResults.map(r => {
                    const sent = sentTo.has(r.id);
                    const name = r.display_name || "Anonymous";
                    return (
                      <div key={r.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 min-w-0">
                        <Avatar className="w-9 h-9 shrink-0">
                          {r.avatar_url && <AvatarImage src={r.avatar_url} alt={name} />}
                          <AvatarFallback className="text-xs font-bold bg-primary/10 text-primary">
                            {name.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-foreground truncate flex-1 min-w-0">{name}</span>
                        <Button
                          size="sm"
                          variant={sent ? "ghost" : "outline"}
                          disabled={sent}
                          onClick={() => handleSend(r.id)}
                          className="shrink-0"
                        >
                          {sent ? <><Check className="w-3 h-3 mr-1" /> Sent</> : <><UserPlus className="w-3 h-3 mr-1" /> Add</>}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              {searched && searchResults.length === 0 && !searching && (
                <p className="text-xs text-muted-foreground">
                  No matches for "{searchQuery}". Try a different name or username.
                </p>
              )}
            </div>

            {/* Friends List */}
            {friends.length === 0 ? (
              <div className="glass-card p-6 sm:p-8 text-center">
                <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-3">
                  Add trusted friends to learn together — or practice now against the Luminar Collective above.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {friends.map(f => {
                  const name = f.friend_profile?.display_name || "Anonymous";
                  const fId = f.friend_profile?.id || "";
                  const open = challengeTarget === fId;
                  return (
                    <div key={f.id} className="glass-card p-3 sm:p-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="w-10 h-10 shrink-0">
                          {f.friend_profile?.avatar_url && <AvatarImage src={f.friend_profile.avatar_url} alt={name} />}
                          <AvatarFallback className="text-sm font-bold bg-primary/10 text-primary">
                            {name.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-heading font-semibold text-foreground truncate flex-1 min-w-0">{name}</span>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => handleCompare(fId, name)} title="Compare Stats" className="h-8 w-8 p-0">
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setChallengeTarget(open ? null : fId)} title="Challenge" className="h-8 w-8 p-0">
                            <Swords className={cn("w-4 h-4", open && "text-primary")} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => removeFriend(f.id)} className="h-8 w-8 p-0 text-destructive hover:text-destructive" title="Remove">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Challenge options — wraps on small screens */}
                      {open && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Pick a quick challenge</p>
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" className="text-xs" onClick={() => handleSendChallenge(fId, "xp_race", 24)}>
                              <Zap className="w-3 h-3 mr-1" /> XP Race · 24h
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs" onClick={() => handleSendChallenge(fId, "xp_race", 48)}>
                              <Zap className="w-3 h-3 mr-1" /> XP Race · 48h
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs" onClick={() => handleSendChallenge(fId, "quiz_duel")}>
                              <BookOpen className="w-3 h-3 mr-1" /> Quiz Duel
                            </Button>
                            <Button size="sm" variant="ghost" className="text-xs" onClick={() => { navigate("/challenges"); }}>
                              More options →
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Comparison panel */}
            {compareId && (
              <div className="glass-card p-4 sm:p-6 border-2 border-primary/30 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-heading font-bold text-foreground truncate">You vs {compareName}</h3>
                  <Button size="sm" variant="ghost" onClick={() => { setCompareId(null); setCompareStats(null); }} className="shrink-0">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                {loadingCompare ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  </div>
                ) : compareStats ? (
                  <div className="space-y-2.5">
                    {/* Header row */}
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-2 sm:gap-3 items-center pb-2 border-b border-border/50">
                      <span className="text-[10px] sm:text-xs uppercase tracking-wider text-primary font-semibold text-right truncate">You</span>
                      <span className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground text-center">vs</span>
                      <span className="text-[10px] sm:text-xs uppercase tracking-wider text-secondary font-semibold truncate">{compareName}</span>
                    </div>
                    {([
                      { label: "Level", you: playerLevel.current_level, them: compareStats.level, icon: Star },
                      { label: "Total XP", you: playerLevel.total_xp, them: compareStats.total_xp, icon: Zap },
                      { label: "Login Streak", you: 0, them: compareStats.login_streak, icon: Flame },
                      { label: "Lessons Done", you: 0, them: compareStats.lessons_completed, icon: BookOpen },
                      { label: "Badges", you: 0, them: compareStats.achievements, icon: Trophy },
                    ] as const).map(stat => {
                      const youWins = stat.you > stat.them;
                      const tie = stat.you === stat.them;
                      return (
                        <div key={stat.label} className="grid grid-cols-[1fr_auto_1fr] gap-2 sm:gap-3 items-center">
                          <span className={cn("text-sm font-semibold text-right tabular-nums truncate", youWins ? "text-primary" : "text-foreground")}>
                            {stat.you.toLocaleString()}
                          </span>
                          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap px-1">
                            <stat.icon className="w-3 h-3 shrink-0" />
                            <span className="hidden sm:inline">{stat.label}</span>
                          </div>
                          <span className={cn("text-sm font-semibold tabular-nums truncate", !youWins && !tie ? "text-secondary" : "text-foreground")}>
                            {stat.them.toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                    {/* Mobile-only stat label legend */}
                    <div className="sm:hidden pt-2 border-t border-border/30 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground justify-center">
                      <span className="flex items-center gap-1"><Star className="w-3 h-3" />Level</span>
                      <span className="flex items-center gap-1"><Zap className="w-3 h-3" />XP</span>
                      <span className="flex items-center gap-1"><Flame className="w-3 h-3" />Streak</span>
                      <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />Lessons</span>
                      <span className="flex items-center gap-1"><Trophy className="w-3 h-3" />Badges</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Could not load stats.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* REQUESTS TAB */}
        {tab === "requests" && (
          <div className="space-y-4">
            {pendingReceived.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">Received</h3>
                {pendingReceived.map(f => {
                  const name = f.friend_profile?.display_name || "Anonymous";
                  return (
                    <div key={f.id} className="glass-card p-3 sm:p-4 flex items-center gap-3 min-w-0">
                      <Avatar className="w-10 h-10 shrink-0">
                        {f.friend_profile?.avatar_url && <AvatarImage src={f.friend_profile.avatar_url} alt={name} />}
                        <AvatarFallback className="text-sm font-bold bg-accent/10 text-accent">
                          {name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-heading font-semibold text-foreground truncate flex-1 min-w-0">{name}</span>
                      <div className="flex gap-1.5 shrink-0">
                        <Button size="sm" onClick={() => acceptRequest(f.id)} className="px-2 sm:px-3">
                          <Check className="w-3 h-3 sm:mr-1" />
                          <span className="hidden sm:inline">Accept</span>
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => declineRequest(f.id)} className="px-2">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {pendingSent.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">Sent</h3>
                {pendingSent.map(f => {
                  const name = f.friend_profile?.display_name || "Anonymous";
                  return (
                    <div key={f.id} className="glass-card p-3 sm:p-4 flex items-center gap-3 min-w-0">
                      <Avatar className="w-10 h-10 shrink-0">
                        {f.friend_profile?.avatar_url && <AvatarImage src={f.friend_profile.avatar_url} alt={name} />}
                        <AvatarFallback className="text-sm font-bold bg-muted/40 text-muted-foreground">
                          {name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="font-heading font-semibold text-foreground truncate">{name}</p>
                        <p className="text-xs text-muted-foreground">Pending…</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {pendingReceived.length === 0 && pendingSent.length === 0 && (
              <div className="glass-card p-6 sm:p-8 text-center">
                <UserPlus className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No pending friend requests.</p>
              </div>
            )}
          </div>
        )}

      {showProfileGate && (
        <OnboardingProfileSetup
          onComplete={async () => {
            setShowProfileGate(false);
            await refetchProfile();
          }}
          onSkip={() => setShowProfileGate(false)}
        />
      )}
    </PageShell>
  );
};

export default FriendsScreen;

type Tone = "primary" | "secondary" | "accent" | "muted";

const TONE_STYLES: Record<Tone, { icon: string; ring: string; glow: string }> = {
  primary:   { icon: "text-primary",   ring: "border-primary/25",   glow: "bg-primary/10" },
  secondary: { icon: "text-secondary", ring: "border-secondary/25", glow: "bg-secondary/10" },
  accent:    { icon: "text-accent",    ring: "border-accent/25",    glow: "bg-accent/10" },
  muted:     { icon: "text-muted-foreground", ring: "border-border", glow: "bg-muted/20" },
};

function SnapshotCard({
  icon: Icon,
  label,
  value,
  sub,
  tone = "primary",
}: {
  icon: AnyValue;
  label: string;
  value: number | string;
  sub?: string;
  tone?: Tone;
}) {
  const s = TONE_STYLES[tone];
  return (
    <div className={cn("glass-card relative overflow-hidden p-3 sm:p-4 border", s.ring)}>
      <div className={cn("absolute -top-10 -right-10 w-24 h-24 rounded-full blur-2xl pointer-events-none", s.glow)} />
      <div className="relative flex items-start gap-2.5">
        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center border", s.ring, s.glow)}>
          <Icon className={cn("w-4 h-4", s.icon)} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
          <p className="text-xl font-heading font-bold text-foreground tabular-nums leading-tight">{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground truncate mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
}
